import { NodeToolbar, Position, useNodeId } from "@xyflow/react";
import classNames from "classnames";
import { Text, ToggleSwitch } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useAtom, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect } from "react";
import {
  ActiveButtonType,
  Actor,
  floatingToolbarActiveButtonAtom,
  NodeType,
  saveCanvasSetter,
  Service,
  sidebarOpenNodeIdAtom,
  toggleZapStepNodesAndEdgesSetterAtomFamily,
  ZapMetadata,
  nodesAtom,
  closeSecondaryToolbarMenuSetter,
  determineAssetType,
} from "@zapier/canvas-state";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import { AppIcon } from "../AppIcon";
import { ButtonOrLinkWithTooltip } from "../ButtonOrLinkWithTooltip";
import { FloatingActorTypeahead } from "./FloatingActorTypeahead";
import { FloatingAppTypeahead } from "./FloatingAppTypeahead";
import { FloatingColorPicker } from "./FloatingColorPicker";
import { FloatingLinkController } from "./FloatingLinkController";
import styles from "./FloatingInlineToolbar.module.css";

type Props = {
  color?: string;
  showAddAppButton?: boolean;
  selectedApps?: Service[];
  actors: Actor[] | undefined;
  hasNotes: boolean;
  hasZap?: boolean;
  isZapExpanded?: boolean;
  isZapNotFound?: boolean;
  zap?: ZapMetadata;
  nodeType: NodeType;
  link?: string;
  assetIds?: {
    tableId?: string;
    interfaceProjectId?: string;
    interfacePageId?: string;
    chatbotId?: string;
    zapId?: string;
  };
};

type LinkTooltipProps = {
  assetType: string | undefined;
  link: string | undefined;
  assetIds:
    | {
        tableId?: string;
        interfaceProjectId?: string;
        interfacePageId?: string;
        chatbotId?: string;
        zapId?: string;
      }
    | undefined;
};

function getLinkButtonTooltip({ assetType, link, assetIds }: LinkTooltipProps) {
  const hasAssetId =
    assetIds && Object.values(assetIds).some((value) => value !== undefined);

  if (hasAssetId && assetType) {
    return `Edit linked ${capitalizeFirstLetter(assetType)}`;
  }

  if (assetType) {
    return `Link existing ${capitalizeFirstLetter(assetType)}`;
  }

  return `${link ? "Edit" : "Add"} link `;
}

function getSecondaryToolbarPosition(activeButton: ActiveButtonType) {
  if (activeButton === "link") {
    return Position.Bottom;
  }

  return Position.Top;
}

function getSecondaryToolbarOffset(activeButton: ActiveButtonType) {
  if (activeButton === "link") {
    return 10;
  }

  return 68;
}

const NO_SECONDARY_MENU_BUTTONS = ["notes", "more"];

export function FloatingInlineToolbar({
  color,
  selectedApps,
  actors,
  hasNotes,
  hasZap,
  isZapExpanded,
  isZapNotFound,
  zap,
  nodeType,
  link,
  assetIds,
}: Props) {
  const id = useNodeId() as string;
  const hasApp = Boolean(selectedApps && selectedApps.length > 0);
  const setSidebarOpenNodeId = useSetAtom(sidebarOpenNodeIdAtom);
  const [activeButton, setActiveButton] = useAtom(
    floatingToolbarActiveButtonAtom,
  );
  const actor = actors?.[0] ?? null;

  const isZapNode = nodeType === "zap";
  const zapSteps = zap?.steps || [];
  const toggleZapStepsAndEdges = useSetAtom(
    toggleZapStepNodesAndEdgesSetterAtomFamily(id),
  );
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const assetType = determineAssetType(selectedApps, nodeType);
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);

  // Or else the secondary menu won't reset when toolbar unmounts
  useEffect(() => {
    return () => {
      closeSecondaryMenu();
    };
  }, [closeSecondaryMenu]);

  useEffect(() => {
    if (isZapExpanded) {
      closeSecondaryMenu(); // Or else this menu will get in the way of the expanded zap steps
    }
  }, [isZapExpanded, closeSecondaryMenu]);

  const toggleSidebar = useCallback(
    (type: ActiveButtonType) => {
      const isAlreadyOpen = type === activeButton;
      setSidebarOpenNodeId(isAlreadyOpen ? "" : id);
    },
    [activeButton, setSidebarOpenNodeId, id],
  );

  const onButtonClick = useCallback(
    (type: ActiveButtonType) => {
      const isAlreadyOpen = type === activeButton;

      setActiveButton(isAlreadyOpen ? null : type);
    },
    [activeButton, setActiveButton],
  );

  const saveZapNodeToBE = useAtomCallback(
    useCallback(
      async (get) => {
        const nodes = get(nodesAtom);
        const updatedNodes = nodes.map((node) => {
          const isZapNode = node.id === id;

          if (!isZapNode) {
            return node;
          }

          return {
            ...node,
            data: {
              ...node.data,
              isExpanded: !isZapExpanded,
            },
          };
        });

        void saveToBackend({ nodes: updatedNodes });
      },
      [id, saveToBackend, isZapExpanded],
    ),
  );

  const toggleZapSteps = useCallback(() => {
    setActiveButton(null);
    toggleZapStepsAndEdges(Boolean(isZapExpanded));
    void saveZapNodeToBE();
  }, [isZapExpanded, saveZapNodeToBE, setActiveButton, toggleZapStepsAndEdges]);

  const componentMapByType = {
    color: <FloatingColorPicker color={color!} />,
    link: (
      <FloatingLinkController
        zap={zap}
        hasZap={hasZap}
        isZapNotFound={isZapNotFound}
        assetType={assetType}
        link={link}
        assetIds={assetIds}
      />
    ),
    app: (
      <FloatingAppTypeahead selectedApps={selectedApps} isZapNode={isZapNode} />
    ),
    actor: <FloatingActorTypeahead actors={actors} />,
  };

  return (
    <>
      {activeButton && (
        <NodeToolbar
          position={getSecondaryToolbarPosition(activeButton)}
          offset={getSecondaryToolbarOffset(activeButton)}
        >
          {!NO_SECONDARY_MENU_BUTTONS.includes(activeButton) &&
            componentMapByType[activeButton as keyof typeof componentMapByType]}
        </NodeToolbar>
      )}
      <NodeToolbar position={Position.Top} offset={15}>
        <div className={styles.floatingToolbarWrapper}>
          <ButtonOrLinkWithTooltip
            content="Edit color"
            onClick={() => onButtonClick("color")}
            className={classNames(styles.buttonNoStyle, {
              [styles.activeButton]: activeButton === "color",
            })}
          >
            <div className={styles.colorDotOuterBorder}>
              <div className={styles.colorDotInnerBorder}>
                <div
                  className={styles.colorDot}
                  style={{ backgroundColor: color }}
                />
              </div>
            </div>
          </ButtonOrLinkWithTooltip>
          <button
            className={classNames(styles.buttonNoStyle, styles.buttonWithText, {
              [styles.activeButton]: activeButton === "app",
            })}
            onClick={() => onButtonClick("app")}
          >
            {hasApp ? (
              <AppIcon app={selectedApps![0]} hasBorder={false} />
            ) : isZapNode ? (
              <div className={styles.zapIconWrapper}>
                <Icon name="miscBoltAltFill" color="BrandOrange" size={16} />
              </div>
            ) : (
              <Icon name="navApps" size={20} />
            )}
            <Text
              type="MinimalPrint3"
              color="TextInverted"
            >{`${hasApp || isZapNode ? "Change" : "Add"} app`}</Text>
          </button>
          <ButtonOrLinkWithTooltip
            content={getLinkButtonTooltip({ assetType, link, assetIds })}
            onClick={() => onButtonClick("link")}
            className={classNames(styles.buttonNoStyle, {
              [styles.activeButton]: activeButton === "link",
            })}
          >
            <Icon name="actionLink" size={20} />
          </ButtonOrLinkWithTooltip>
          <ButtonOrLinkWithTooltip
            content={`${hasNotes ? "Edit" : "Add"} note`}
            onClick={() => {
              onButtonClick("note");
              toggleSidebar("note");
            }}
            className={classNames(styles.buttonNoStyle, {
              [styles.activeButton]: activeButton === "note",
            })}
          >
            <Icon name="fileFilled" size={20} />
          </ButtonOrLinkWithTooltip>
          <ButtonOrLinkWithTooltip
            content={`${actor ? "Edit" : "Add"} people involved`}
            onClick={() => onButtonClick("actor")}
            className={classNames(styles.buttonNoStyle, {
              [styles.activeButton]: activeButton === "actor",
            })}
          >
            <Icon name="personCircle" size={20} />{" "}
          </ButtonOrLinkWithTooltip>
          {zapSteps && zapSteps.length > 0 && (
            <div className={classNames(styles.flexContainer)}>
              <ToggleSwitch checked={isZapExpanded} onChange={toggleZapSteps} />
              <Text type="Body1" color="TextInverted">
                Show steps
              </Text>
            </div>
          )}
          <ButtonOrLinkWithTooltip
            content="More options"
            onClick={() => {
              onButtonClick("more");
              toggleSidebar("more");
            }}
            className={classNames(styles.buttonNoStyle, styles.noDivider, {
              [styles.activeButton]: activeButton === "more",
            })}
          >
            <Icon name="navMoreHoriz" size={20} />
          </ButtonOrLinkWithTooltip>
        </div>
      </NodeToolbar>
    </>
  );
}
